import {saveFocusRating,focusRatingEntries} from '../../utils/focusRatings';
import DesktopAwareNotification from "../core-components/DesktopAwareNotification";
import PointHelp from "../planning/PointHelp";
import FocusPointOrderControls from '../planning/FocusPointOrderControls';
import ImprovementPointsList from '../planning/ImprovementPointsList';
import {pointIdentity} from '../../utils/improvementPoints';
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  writeBatch,
} from "firebase/firestore";
import { Pencil, Pin } from "lucide-react";

import { auth, db } from "../../Security/firebase-config";
import { useTeam } from "../../context/TeamContext";
import StarRating from "./StarRatings";
import {ClassificationDisplay} from '../planning/NoteClassification';
import {noteClassification} from '../../utils/noteClassification';

import "./FocusPoints.css";

const FOCUS_POINT_OUTCOMES = [
  { value: "not_attempted", label: "Not attempted" },
  { value: "improving", label: "Improving" },
  { value: "achieved", label: "Achieved" },
  { value: "needs_rework", label: "Needs rework" },
];
const EMPTY_FOCUS_POINTS = [];

function normalizeFocusPointOutcome(value) {
  return FOCUS_POINT_OUTCOMES.some((outcome) => outcome.value === value)
    ? value
    : "not_attempted";
}

function formatFocusPointOutcome(value) {
  return FOCUS_POINT_OUTCOMES.find((outcome) => outcome.value === value)?.label || "Not attempted";
}

function createDraftFocusPoint(text = "") {
  return {
    id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text,
    ratings: {},
  };
}

function normalizeFocusPointRatings(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((ratings, [username, rawRating]) => {
    const normalizedUsername = String(username || "").trim();
    const normalizedRating = Number(rawRating);

    if (
      !normalizedUsername ||
      !Number.isFinite(normalizedRating) ||
      normalizedRating < 1 ||
      normalizedRating > 5
    ) {
      return ratings;
    }

    ratings[normalizedUsername] = Math.round(normalizedRating);
    return ratings;
  }, {});
}

function cloneFocusPoints(points) {
  return Array.isArray(points)
    ? points.map((point) => ({
        ...point,
        id: point?.id || createDraftFocusPoint().id,
        text: typeof point?.text === "string" ? point.text : "",
        ratings: normalizeFocusPointRatings(point?.ratings ?? point?.rating),
        outcome: normalizeFocusPointOutcome(point?.outcome),
        outcomeNote: typeof point?.outcomeNote === "string" ? point.outcomeNote : "",
        ...noteClassification(point),
          sourceType:point.sourceType || "improvement_note",sourceId:point.sourceId || null,sourceScope:point.sourceScope || point.scope || "general",sourceMapId:point.sourceMapId || point.mapId || null,
      }))
    : [];
}

function sanitizeFocusPoints(points) {
  return cloneFocusPoints(points)
    .map((point) => ({
      ...point,
      text: point.text.trim(),
    }))
    .filter((point) => point.text.length > 0);
}

function reorderFocusPoints(points, dragIndex, dropIndex) {
  if (dragIndex === dropIndex) {
    return points;
  }

  const nextPoints = [...points];
  const [movedPoint] = nextPoints.splice(dragIndex, 1);
  if (!movedPoint) {
    return points;
  }

  nextPoints.splice(dropIndex, 0, movedPoint);
  return nextPoints;
}

export default function FocusPoints({
  focusPoints = EMPTY_FOCUS_POINTS,
  videoId,
  matchId = null,
  mapId,
  teamId,
  disabled = false,
  showRatings = true,
  showOutcomes = showRatings,
  onDraftChange,
  alwaysEditable = false,
  showEmptyPlaceholder = false,
  instantDraft = false,
  instantSave = false,
  returnToLibrary = false,
  confirmRemoval = false,
  libraryOnlyWhenEditing = false,
}) {
  const [editing, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const editMode = alwaysEditable || editing;
  const [editedPoints, setEditedPoints] = useState(() => cloneFocusPoints(focusPoints));
  const { username } = useTeam();
  const isDraftMode = typeof onDraftChange === "function";

  useEffect(() => {
    setEditedPoints(cloneFocusPoints(focusPoints));
  }, [focusPoints]);

  const focusPointsCollectionRef = useMemo(() => {
    if (!teamId || !mapId) return null;
    if (matchId) {
      return collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "focusPoints");
    }
    if (!videoId) {
      return null;
    }
    return collection(db, "teams", teamId, "maps", mapId, "videos", videoId, "focusPoints");
  }, [mapId, matchId, teamId, videoId]);

  const title = returnToLibrary ? "Focus Points" : matchId ? "Match Focus Points (Shared across POVs)" : "VOD Focus Points";
  const subtitle = returnToLibrary ? "Rank the review priorities that should be saved with this VOD." : matchId
    ? "Shared across all POVs in the linked review match."
    : "Specific to this standalone review upload.";
  const normalizedUsername = String(username || "").trim();
  const canEdit = !disabled && !saving && (isDraftMode || !!focusPointsCollectionRef);

  function beginEditing() {
    if (!canEdit) return;
    setEditMode(true);
  }

  function movePoint(index,offset) {
    if(!editMode || !canEdit)return;
    changePoints(reorderFocusPoints(editedPoints,index,index+offset));
  }

  function changePoints(nextPoints) {
    setEditedPoints(nextPoints);
    if (instantDraft && isDraftMode) onDraftChange(sanitizeFocusPoints(nextPoints));
    if (instantSave && !isDraftMode && focusPointsCollectionRef) {
      setSaving(true);
      setSaveError("");
      void handleSave(nextPoints).catch(error => {
        setSaveError(error instanceof Error ? error.message : "Could not save Focus Points. Please try again.");
      }).finally(() => setSaving(false));
    }
  }

  function handleCancel() {
    setEditedPoints(cloneFocusPoints(focusPoints));
    setEditMode(false);
  }

  async function handleSave(pointsToSave = editedPoints) {
    const nextPoints = sanitizeFocusPoints(pointsToSave);

    if (isDraftMode) {
      onDraftChange(nextPoints);
      setEditedPoints(nextPoints);
      setEditMode(false);
      return;
    }

    if (!focusPointsCollectionRef) return;

    const batch = writeBatch(db);
    const originalIds = cloneFocusPoints(focusPoints).map((point) => point.id).filter(Boolean);
    const keptIds = nextPoints
      .map((point) => point.id)
      .filter((id) => typeof id === "string" && !id.startsWith("temp-"));

    originalIds
      .filter((id) => !keptIds.includes(id))
      .forEach((id) => batch.delete(doc(focusPointsCollectionRef, id)));

    nextPoints.forEach((point, index) => {
      if (String(point.id || "").startsWith("temp-")) {
        const nextDocRef = doc(focusPointsCollectionRef);
        batch.set(nextDocRef, {
          order: index,
          text: point.text,
          ...noteClassification(point),
          sourceType:point.sourceType || "improvement_note",sourceId:point.sourceId || null,sourceScope:point.sourceScope || point.scope || "general",sourceMapId:point.sourceMapId || point.mapId || null,
          ratings: {},
          outcome: normalizeFocusPointOutcome(point.outcome),
          outcomeNote: point.outcomeNote || "",
        });
        return;
      }

      batch.set(
        doc(focusPointsCollectionRef, point.id),
        {
          order: index,
          text: point.text,
          ...noteClassification(point),
          sourceType:point.sourceType || "improvement_note",sourceId:point.sourceId || null,sourceScope:point.sourceScope || point.scope || "general",sourceMapId:point.sourceMapId || point.mapId || null,
          outcome: normalizeFocusPointOutcome(point.outcome),
          outcomeNote: point.outcomeNote || "",
        },
        { merge: true },
      );
    });

    await batch.commit();

    const refreshed = await getDocs(query(focusPointsCollectionRef, orderBy("order", "asc")));
    setEditedPoints(refreshed.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
    setEditMode(false);
  }

  async function handleRate(pointId, nextRating) {
    if (!showRatings || !normalizedUsername || !focusPointsCollectionRef) return;

    const pointRef = doc(focusPointsCollectionRef, pointId);
    const saved = await saveFocusRating(db,pointRef,{uid:auth.currentUser?.uid,username:normalizedUsername},nextRating);
    setEditedPoints((current) =>
      current.map((point) =>
        point.id === pointId
          ? {
              ...point,
              ...saved,
            }
          : point,
      ),
    );
  }

  async function importPoint(point) {
    if (editedPoints.some(existing=>pointIdentity(existing)===pointIdentity(point))) return;
    const next=[...editedPoints,point];
    if (editMode) {changePoints(next);return;}
    if (isDraftMode) {onDraftChange(next);setEditedPoints(next);return;}
    if (!focusPointsCollectionRef) return;
    const ref=doc(focusPointsCollectionRef);
    const {id,...data}=point;
    const batch=writeBatch(db);batch.set(ref,{...data,order:editedPoints.length});await batch.commit();
    setEditedPoints([...editedPoints,{...point,id:ref.id}]);
  }
  return (
    <div className={`ImprovementFocusLayout ${libraryOnlyWhenEditing && !editMode ? "ImprovementFocusLayout--focus-only" : ""}`}>{(!libraryOnlyWhenEditing || editMode) && <ImprovementPointsList teamId={teamId} mapId={mapId} selectedFocus={editedPoints} disabled={!canEdit} onAddFocus={importPoint}/>}
    <section className={`review-focus-point-shell ${returnToLibrary ? "review-focus-point-shell--vod-draft" : ""} ${!showOutcomes ? "review-focus-point-shell--without-outcomes" : ""}`}>
      <div className="review-focus-point-shell__header">
        <div className="review-focus-point-shell__copy">
          <div className="review-focus-point-shell__title">
            <Pin size={16} aria-hidden="true" />
            <h3>{title}<PointHelp kind="focus"/></h3>
          </div>
          <p className={matchId && !returnToLibrary ? "review-focus-point-shell__subtitle--shared" : undefined}>{subtitle}</p>
        </div>

        {!instantDraft && !instantSave && <div className="review-focus-point-shell__actions">
          {!editMode ? (
            <button
              type="button"
              className={`review-focus-point-shell__action-button ${libraryOnlyWhenEditing ? "review-focus-point-shell__action-button--icon-only" : ""}`}
              aria-label="Edit Focus Points"
              title="Edit Focus Points"
              onClick={beginEditing}
              disabled={!canEdit}
            >
              <Pencil className="review-focus-point-shell__edit-icon" size={16} aria-hidden="true" />
              {!libraryOnlyWhenEditing && <span className="review-focus-point-shell__edit-label">Edit</span>}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="review-focus-point-shell__action-button review-focus-point-shell__action-button--primary"
                onClick={() => void handleSave()}
                disabled={disabled}
              >
                Save Changes
              </button>
              
              <button
                type="button"
                className="review-focus-point-shell__action-button app-cancel-control"
                onClick={handleCancel}
                disabled={disabled}
              >
                Cancel
              </button>
            </>
          )}
        </div>}
      </div>

      {editedPoints.length ? (
        <ol
          className={`review-focus-point-shell__list ${
            editMode ? "review-focus-point-shell__list--editable" : ""
          }`.trim()}
        >
          {editedPoints.map((point, index) => {
            const ratingsMap = normalizeFocusPointRatings(point.ratings ?? point.rating);
            const ratingValues = focusRatingEntries(point).map(row=>row.value);
            const userRating = normalizedUsername ? point.ratingsByUid?.[auth.currentUser?.uid]?.value || ratingsMap[normalizedUsername] || 0 : 0;
            const averageRating =
              ratingValues.length > 0
                ? ratingValues.reduce((sum, value) => sum + value, 0) / ratingValues.length
                : null;
            const outcome = normalizeFocusPointOutcome(point.outcome);

            return (
              <li
                key={point.id}
                className={`review-focus-point-shell__row ${
                  editMode ? "FocusPointRow--editable" : "review-focus-point-shell__row--video-list-rating"
                }`.trim()}

              >
                {editMode ? (
                  <>
                    <FocusPointOrderControls confirmRemoval={confirmRemoval} returnToLibrary={returnToLibrary} index={index} count={editedPoints.length} disabled={!canEdit} onMove={offset=>movePoint(index,offset)} onDelete={()=>changePoints(editedPoints.filter(entry=>entry.id!==point.id))}/>
                    <div className="FocusPointRow__content"><ClassificationDisplay value={point} showScope={false}/><p><span className="review-focus-point-shell__number">{index+1}.</span> {point.text}</p></div>
                    {showOutcomes ? (
                      <label className="review-focus-point-shell__outcome-edit">
                        <span>Outcome</span>
                        <select
                          value={outcome}
                          onChange={(event) =>
                            setEditedPoints((current) =>
                              current.map((entry) =>
                                entry.id === point.id
                                  ? { ...entry, outcome: event.target.value }
                                  : entry,
                              ),
                            )
                          }
                          disabled={disabled}
                        >
                          {FOCUS_POINT_OUTCOMES.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}

                  </>
                ) : (
                  showRatings ? (
                    <div className="review-focus-point-shell__display review-focus-point-shell__display--video-list-rating">
                      <span className="review-focus-point-shell__number review-focus-point-shell__number--inline">
                        {index + 1}.
                      </span>
                      <span className="review-focus-point-shell__text">{point.text}</span>
                      <ClassificationDisplay value={point} showScope={false}/>
                      {showOutcomes ? (
                        <span className={`review-focus-point-shell__outcome is-${outcome}`}>
                          {formatFocusPointOutcome(outcome)}
                        </span>
                      ) : null}
                      <StarRating
                        rating={userRating}
                        averageRating={averageRating}
                        ratingCount={ratingValues.length}
                        onRate={(nextRating) => handleRate(point.id, nextRating)}
                        disabled={disabled}
                      />
                    </div>
                  ) : (
                    <div className="review-focus-point-shell__display review-focus-point-shell__display--text-only">
                      <span className="review-focus-point-shell__number review-focus-point-shell__number--inline">
                        {index + 1}.
                      </span>
                      <span className="review-focus-point-shell__text">{point.text}</span>
                      <ClassificationDisplay value={point} showScope={false}/>
                      {showOutcomes ? (
                        <span className={`review-focus-point-shell__outcome is-${outcome}`}>
                          {formatFocusPointOutcome(outcome)}
                        </span>
                      ) : null}
                    </div>
                  )
                )}
              </li>
            );
          })}
        </ol>
      ) : returnToLibrary ? (
        <ol className="review-focus-point-shell__list">
          <li className="review-focus-point-shell__row review-focus-point-shell__guidance">
            <div className="review-focus-point-shell__placeholder-meta" aria-hidden="true"><span/><span/></div>
            <div><strong>Choose a Focus Point</strong>
              <p>Focus Points are the improvements you and your team will prioritize in this match. Find an improvement in the list on the left and press its right arrow to add it here. Order your Focus Points by priority, then rate how well you applied them after the match.</p>
              <p>The left arrow removes a point from this VOD’s focus points while keeping it in the Improvement Points List.</p>
            </div>
          </li>
        </ol>
      ) : showEmptyPlaceholder && !editMode ? (
        <ol className="review-focus-point-shell__list">
          <li className="review-focus-point-shell__row">
            <button
              type="button"
              className="review-focus-point-shell__display review-focus-point-shell__display--text-only review-focus-point-shell__placeholder"
              disabled={!canEdit}
              onClick={beginEditing}
            >
              <span className="review-focus-point-shell__number review-focus-point-shell__number--inline">1.</span>
              <span className="review-focus-point-shell__text">No focus points added yet.</span>
            </button>
          </li>
        </ol>
      ) : (
        <div className="review-focus-point-shell__empty">
          Select improvement points from the list to add Focus Points.
        </div>
      )}
      {saveError && <DesktopAwareNotification as="p" role="alert">{saveError}</DesktopAwareNotification>}
    </section></div>
  );
}
